import {
  Button,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';

import { useI18n } from '../../../../../../application/language/useI18n';
import { ContainerContext } from '../../../../../../common/container/ContainerContext';
import { UserSystemRoleModelPermissionMap } from '../../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';
import { AutocompleteSelectItem } from '../../../../../design/form/FormAutocomplete';
import { FormAutocompleteStandard } from '../../../../../design/form/FormAutoCompleteStandard';
import { PageEmptyContent } from '../../../../../design/page/PageEmptyContent';
import { ProjectTeamSpaceExtensions } from '../inject/ProjectTeamSpaceExtensions';
import { IProjectTeamMemberView } from '../store/model/IProjectTeamView';
import { ProjectInfoDomain } from '../store/ProjectInfoDomain';


export interface IProjectTeamTableProperties {
  domain: ProjectInfoDomain;
  team: IProjectTeamMemberView[];
  projectId: string;
  isEmptyApplications: boolean;
}

export const ProjectTeamTable = observer(
  ({ domain, team, projectId, isEmptyApplications }: IProjectTeamTableProperties) => {
    const container = useContext(ContainerContext);
    const extensions = container.get(ProjectTeamSpaceExtensions);
    const teamContent = domain.ui.teamTableRowsContent.list.find((item) => item.projectId === projectId);
    const { translate, translateAnyway } = useI18n();

    const isInvitedUser = !!domain.ui.userJoininInfo.entity;
    const isUserLeft = domain.ui.hasUserLeftProject.value;
    const possibleRoles: AutocompleteSelectItem[] = (domain.ui.userJoininInfo.entity?.link?.abledRoles.map(({ id, name }) => ({ value: id, name })) ?? []);

    React.useEffect(() => {
      return () => {
        domain.activateJoiningAction()
      }
    }, [domain])

    React.useEffect(() => {
      const callActionBeforeLeave = () => {
        domain.activateJoiningAction();
      };

      window.addEventListener('beforeunload', callActionBeforeLeave)
      return () => {
        window.removeEventListener('beforeunload', callActionBeforeLeave)
      }
    }, [domain])

    return (
      <Grid item>
        <Grid container direction={'column'} spacing={2}>
          {(teamContent?.data?.length || 0) > 0 && (
            <Grid item xs={5}>
              <TableContainer>
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      {domain.ui.teamTableRowsHeaders.list.map((header) => (
                        <TableCell key={header.key}>{translateAnyway(`projectPage.${header.title}`)}</TableCell>
                      ))}
                      <TableCell key="action" />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {teamContent?.data.map((content) => {
                      const isCurrentUserRow = content.memberId === domain.layoutDomain.ui.activeUser.entity.id;

                      return (
                        <TableRow sx={{ height: '82px' }} key={`${content.memberId}-${teamContent.projectId}`}>
                          {content.data.map((dataItem, index) => {
                            const headerKey = domain.ui.teamTableRowsHeaders.list[index]?.key;

                            const width = headerKey === 'projectRole' ? "320px" : dataItem.width;
                            if (headerKey === 'projectRole' && isInvitedUser && isCurrentUserRow) {
                              if (isUserLeft) {
                                return (
                                  <TableCell key={`${content.memberId}-${headerKey}`} style={{ width: width }}>
                                    {translate('projectPage.youLeftProject')}
                                  </TableCell>
                                );
                              }

                              if (possibleRoles.length > 1) {
                                return (
                                  <TableCell key={`${content.memberId}-${headerKey}`} style={{ width: width }}>
                                    <FormAutocompleteStandard
                                      size="small"
                                      label={translate('projectPage.selectRole')}
                                      values={possibleRoles}
                                      selected={domain.ui.userRoleByInvitation.value}
                                      onChangeSelect={(newValue) => {
                                        domain.setUserRole(newValue?.value ?? null);
                                      }}
                                    />
                                  </TableCell>
                                );
                              }
                            }

                            return (
                              <TableCell key={`${content.memberId}-${headerKey}`} style={{ width: width }}>
                                {dataItem.content}
                              </TableCell>
                            );
                          })}

                          <TableCell style={{ width: '282px' }} align={'right'} key={`action-${content.memberId}`}>
                            {isCurrentUserRow && isInvitedUser && possibleRoles.length > 0 && (
                              <Button
                                variant="text"
                                color={isUserLeft ? 'info' : 'error'}
                                onClick={() => domain.toggleLeftProject()}
                              >
                                {isUserLeft
                                  ? translate('projectPage.returnToProject')
                                  : translate('projectPage.leaveProject')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          {!isEmptyApplications && (teamContent?.data?.length || 0) === 0 && (
            <Grid item xs={2}>
              <PageEmptyContent text={translate('projectPage.noTeamForProject')} isFullPage={false} />
            </Grid>
          )}

          {extensions.map((ExtensionComponent, idx) => (
            <Grid item xs={5} key={idx}>
              <ExtensionComponent domain={domain} projectId={projectId} />
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  textDivider: {
    width: 100,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));
